import { useMemo } from 'react';
import { Combine, Replace } from 'src/types/common';

/**
 * Helper to memoize dictionary of hooks
 *
 * This is useful for controller (or similar) hooks, which return a collection
 * of other hooks. If the returned dictionary is not destructured in the caller
 * component immediately, it needs to be memoized,
 *
 * IMPORTANT: the object properties must be stable
 */
export const useHookMemo = <T extends object>(hooks: T): T => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => hooks, Object.values(hooks));
};

/**
 * Helper to injecting additional dependencies to dictionary of hooks
 *
 * This allows using a (possible unstable) dictionary to be extended with the
 * same mechanism as the useHookMemo, while avoiding changes in dependencies.
 *
 * IMPORTANT: base must be correctly memoized
 */
export const useInjectHook = <Base, T extends object>(
  base: Base,
  hooks: T
): Base & T =>
  useMemo(
    () => ({ ...base, ...hooks }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...Object.values(hooks), base]
  );

/**
 * Specific helper to inject additional extra dependencies
 *
 * This is a specialization of useExtendedHookMemo for the table store or item
 * store contexts, which provide an extra property that is populated over
 * multiple layers.
 *
 * IMPORTANT: base must be correctly memoized
 */
export const useInjectExtra = <
  Base extends { extra: object | undefined },
  T extends object
>(
  { extra, ...base }: Base,
  hooks: T
): Replace<Base, 'extra', Combine<Base['extra'], T>> =>
  useMemo(
    () => ({ ...base, extra: { ...extra, ...hooks } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...Object.values(hooks), base]
  );
